class AddToCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.updateButtonState();

    const productContainer =
      this.closest(".product-information") ||
      this.closest("[data-product-data]");

    const variantChangeHandler = (event) => {
      if (event.detail) {
        if (event.detail.selectedVariant) {
          const variantId = event.detail.selectedVariant.id;
          const isAvailable = event.detail.selectedVariant.available;

          this.dataset.variantId = variantId;
          this.dataset.available = isAvailable.toString();
          this.dataset.variantSelected = "true";
          this.dataset.colorSoldOut = "false";
        } else {
          this.dataset.variantSelected = "false";
          // Check if the color itself is sold out (no available sizes)
          this.dataset.colorSoldOut = event.detail.isColorSoldOut
            ? "true"
            : "false";
        }
        this.updateButtonState();
      }
    };

    document.addEventListener("variant:changed", variantChangeHandler);

    if (productContainer) {
      productContainer.addEventListener(
        "variant:changed",
        variantChangeHandler
      );
    }

    this.addEventListener("click", async (e) => {
      e.preventDefault();

      // Sold-out (colour or selected size): open the notify-me modal instead
      // of the add-to-cart flow.
      if (this.classList.contains("notify-me")) {
        const notifyVariantId = this.dataset.variantId;
        window.openNotifyMeModal?.(
          notifyVariantId ? Number(notifyVariantId) : null
        );
        return;
      }

      const variantSelected = this.dataset.variantSelected === "true";

      if (!variantSelected) {
        this.showSizeNotSelectedMessage();
        this.scrollToVariantSelection();
        return;
      }

      if (this.dataset.available === "false") {
        return;
      }

      const variantId = this.dataset.variantId;
      const productId = this.dataset.productId;

      if (!variantId) {
        console.warn("Missing data-variant-id on <add-to-cart-button>");
        return;
      }

      if (!productId) {
        console.warn("Missing data-product-id on <add-to-cart-button>");
        return;
      }

      if (this.loading) return;
      this.loading = true;

      const originalHTML = this.innerHTML;
      const loadingHTML = this.dataset.loading;

      this.innerHTML = loadingHTML;

      try {
        let currentCart = { items: [], item_count: 0 };
        try {
          currentCart = await fetch("/cart.js").then((r) => r.json());
        } catch (err) {
          console.error("Error fetching current cart:", err);
        }

        await this.updateExistingCartItems(currentCart);

        const variantIdNum = parseInt(variantId, 10);

        if (isNaN(variantIdNum)) {
          console.error("Invalid variant ID:", variantId);
          throw new Error("Invalid variant ID");
        }

        const MAX_PER_VARIANT = 5;

        try {
          const freebieVariantId = window.FREEBIE_CONFIG?.variantId
            ? Number(window.FREEBIE_CONFIG.variantId)
            : null;

          const isFreebieItem =
            freebieVariantId && variantIdNum === freebieVariantId;

          if (!isFreebieItem) {
            // Calculate total quantity of this variant across all cart lines
            const existingTotalQty = currentCart.items
              .filter(
                (it) =>
                  Number(it.variant_id) === variantIdNum ||
                  Number(it.id) === variantIdNum
              )
              .reduce((sum, it) => sum + (it.quantity || 0), 0);

            if (existingTotalQty >= MAX_PER_VARIANT) {
              this.showToast(
                "You already have the maximum quantity of this product variant in your cart.",
                "warning"
              );
              window.dispatchEvent(new CustomEvent("cart:open"));
              this.innerHTML = originalHTML;
              this.loading = false;
              return;
            }

            if (existingTotalQty + 1 > MAX_PER_VARIANT) {
              this.showToast(
                `Maximum quantity for this product variant is ${MAX_PER_VARIANT}.`,
                "warning"
              );
              window.dispatchEvent(new CustomEvent("cart:open"));
              this.innerHTML = originalHTML;
              this.loading = false;
              return;
            }
          }
        } catch (err) {
          console.error("Error checking cart quantity:", err);
        }

        window.dispatchEvent(new CustomEvent("cart:loading"));

        const mainItem = { id: variantIdNum, quantity: 1 };
        const itemsToAdd = [mainItem];

        if (window.FREEBIE_CONFIG?.enabled && window.FREEBIE_CONFIG.variantId) {
          const freebieVariantId = Number(window.FREEBIE_CONFIG.variantId);

          // Check if freebie already exists in cart
          const hasFreebie = currentCart.items.some(
            (item) =>
              Number(item.id) === freebieVariantId ||
              Number(item.variant_id) === freebieVariantId
          );

          // Only add freebie if it doesn't already exist in the cart
          if (!hasFreebie) {
            const freebieItem = { id: freebieVariantId, quantity: 1 };
            itemsToAdd.push(freebieItem);
          }
        }

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: itemsToAdd,
            sections: ["cart-drawer"],
            sections_url: window.location.pathname,
          }),
        });

        if (!response.ok) {
          let errorMessage = `HTTP error! status: ${response.status}`;
          try {
            const errorData = await response.json();
            errorMessage =
              errorData.description || errorData.message || errorMessage;

            if (response.status === 422) {
              this.showToast(errorMessage, "warning");
              this.innerHTML = originalHTML;
              this.loading = false;
              return;
            }
          } catch (e) {
            console.error("Error:", e);
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        const cartResponse = await fetch("/cart.js");
        const cart = await cartResponse.json();

        window.dispatchEvent(
          new CustomEvent("cart:count-update", {
            detail: {
              count: cart.item_count,
            },
          })
        );

        window.dispatchEvent(
          new CustomEvent("cart:updated", {
            detail: { cart, item: data, sections: data.sections },
          })
        );

        document.dispatchEvent(
          new CustomEvent("cart:item-added", { detail: { cart, item: data } })
        );

        window.dispatchEvent(new CustomEvent("cart:open"));
        window.dispatchEvent(new CustomEvent("cart:loaded"));
      } catch (error) {
        try {
          if (error && error.message) {
            this.showToast(error.message, "error");
          }
        } catch (err) {}
        console.error("Add to cart failed:", error);
      } finally {
        this.innerHTML = originalHTML;
        this.loading = false;
        window.dispatchEvent(new CustomEvent("cart:loaded"));
      }
    });
  }

  async updateExistingCartItems(cart) {
    try {
      window.dispatchEvent(
        new CustomEvent("cart:count-update", {
          detail: {
            count: cart.item_count,
          },
        })
      );

      const updatePromises = cart.items.map((item, index) => {
        if (item.properties && item.properties.is_last === "true") {
          const updatedProperties = { ...item.properties };
          updatedProperties.is_last = "false";

          return fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              line: index + 1,
              properties: updatedProperties,
            }),
          });
        }
        return Promise.resolve();
      });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error("Error updating existing cart items:", error);
    }
  }

  updateButtonState() {
    const isAvailable = this.dataset.available !== "false";
    const variantId = this.dataset.variantId;
    const variantSelected = this.dataset.variantSelected === "true";
    const colorSoldOut = this.dataset.colorSoldOut === "true";

    // Notify-Me state: the whole colour is sold out, or a specific selected
    // size is sold out (a real variant exists to subscribe to). The button
    // becomes a functional "NOTIFY ME" trigger instead of a dead "SOLD OUT".
    const showNotify =
      colorSoldOut || (variantSelected && !isAvailable && !!variantId);

    this.classList.toggle("notify-me", showNotify);
    this.classList.toggle(
      "unavailable",
      !isAvailable && variantSelected && !showNotify
    );

    if (showNotify) {
      // Clickable — the click handler opens the notify-me modal
      this.removeAttribute("disabled");
      this.removeAttribute("aria-disabled");
      this.style.pointerEvents = "";
      this.textContent = "NOTIFY ME";
    } else if (!variantSelected) {
      this.removeAttribute("disabled");
      this.removeAttribute("aria-disabled");
      this.style.pointerEvents = "";
      this.textContent = "ADD TO CART";
    } else if (!isAvailable) {
      // No matching variant exists for this selection — nothing to notify about
      this.setAttribute("disabled", "");
      this.setAttribute("aria-disabled", "true");
      this.textContent = "UNAVAILABLE";
      this.style.pointerEvents = "none";
    } else {
      this.removeAttribute("disabled");
      this.removeAttribute("aria-disabled");
      this.style.pointerEvents = "";
      this.textContent = "ADD TO CART";
    }
  }

  showSizeNotSelectedMessage() {
    const variantSelects = document.querySelector("variant-selects");

    if (variantSelects && variantSelects.showSizeNotSelectedMessage) {
      variantSelects.showSizeNotSelectedMessage();
    } else {
      const messageElement = document.getElementById(
        "size-not-selected-message"
      );
      if (messageElement) {
        messageElement.classList.add("show");
      }
    }
  }

  scrollToVariantSelection() {
    const productContainer =
      this.closest(".product-information") ||
      this.closest("[data-product-data]");

    if (!productContainer) return;

    const variantSelector = productContainer.querySelector(
      ".product-sizing-guide"
    );

    if (variantSelector) {
      variantSelector.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }

  showToast(message, type = "info") {
    try {
      const container = document.querySelector(".global-toast-container");
      if (!container) return;
      const toast = document.createElement("div");
      toast.className = `global-toast global-toast--${type}`;
      toast.textContent = message;
      container.appendChild(toast);
      setTimeout(() => toast.classList.add("global-toast--visible"), 50);
      setTimeout(() => {
        toast.classList.remove("global-toast--visible");
        setTimeout(() => toast.remove(), 300);
      }, 4000);
    } catch (err) {
      console.warn("Toast error", err);
    }
  }
}

customElements.define("add-to-cart-button", AddToCart);
